import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import MigrationHelper from '../MigrationHelper';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface RiskAssessments {
  readonly componentRiskResultId?: string;
  readonly fkComponentFailure?: string;
  readonly componentRiskCategoryRankType?: number;
  readonly riskType?: number;
  readonly riskValue?: number;
  readonly assetID?: number;
  readonly riskName?: string;
  readonly inspectionId?: string;
  readonly name?: string;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly fkDeficiencyUUID: string;
  readonly pkComponentRiskUUID: string;
  readonly fkInspectorField?: number;
  readonly fkInspectorLead?: number;
  readonly siteId?: number;
  readonly disciplineId?: number;
  readonly activeDeficiencyInspectionId?: number;
  isChanged?: boolean;
}

export interface RiskAssessmentsAdditionalFields extends AdditionalFields {
  siteId?: number;
  disciplineId?: number;
  componentRiskResultId?: string;
}

export const riskAssessmentSchema: RxJsonSchema<RiskAssessments> = {
  type: 'object',
  version: 1,
  description: 'describes deficiency lists',
  primaryKey: 'pkComponentRiskUUID',
  properties: {
    pkComponentRiskUUID: { type: 'string', maxLength: 36 },
    componentRiskResultId: { type: ['string', 'null'] },
    fkDeficiencyUUID: { type: ['string', 'null'] },
    fkComponentFailure: { type: ['string', 'null'] },
    componentRiskCategoryRankType: { type: ['number', 'null'] },
    riskType: { type: ['number', 'null'] },
    riskValue: { type: ['number', 'null'] },
    assetID: { type: ['number', 'null'] },
    riskName: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    fkInspectorField: { type: ['number', 'null'] },
    fkInspectorLead: { type: ['number', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    activeDeficiencyInspectionId: { type: ['number', 'null'] },
  },
};

export const riskAssessmentMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: RiskAssessments) => {
    return MigrationHelper.instance.migrateRiskV1(oldDoc).then(response => {
      if (response) return { ...oldDoc, ...response };
      //if response if failed do not migrate data.
      return null;
    });
  },
};

export type RiskAssessmentCollection = RxCollection<RiskAssessments> | null;
export type RiskAssessmentDocument = RxDocument<RiskAssessments>;

export const riskAssessmentPullQueryBuilder = (document: RiskAssessments) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query componentRiskFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime!
      $isLeadInspector: Boolean!
    ) {
      componentRiskFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        componentRiskResultId
        fkComponentFailure
        componentRiskCategoryRankType
        riskType
        riskValue
        riskName
        assetID
        name
        inspectionId
        dateCreated
        dateModified
        fkDeficiencyUUID
        pkComponentRiskUUID
        fkInspectorField
        fkInspectorLead
        siteId
        disciplineId
        activeDeficiencyInspectionId
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc?.siteId,
        doc?.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const riskAssessmentPushQueryBuilder = (doc: RiskAssessmentDocument[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation mobileAddOrUpdateComponentRiskResult(
      $role: Int!
      $MobileComponentRiskFeedInput: MobileComponentRiskFeedInput
    ) {
      mobileAddOrUpdateComponentRiskResult(
        role: $role
        componentRiskResults: $MobileComponentRiskFeedInput
      ) {
        status
      }
    }
  `);

  const role = getRoleForMutation(document);
  const variables = {
    role,
    MobileComponentRiskFeedInput: document,
  };
  return {
    query,
    variables,
  };
};

export const riskAssessmentPushModifier = (doc: RiskAssessmentDocument) => {
  const obj: RiskAssessmentDocument & RiskAssessmentsAdditionalFields = {
    ...doc,
  };
  if (obj.componentRiskResultId === '') {
    obj.componentRiskResultId = undefined;
  }
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'dateCreated',
    'dateModified',
    'siteId',
    'disciplineId',
    'isDeleted',
    '_meta',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof RiskAssessmentsAdditionalFields] = undefined;
  });
  return obj;
};

export default {};
